import React, { useCallback, useContext, useEffect, useMemo } from 'react';
import { makeStyles, Theme } from '@material-ui/core/styles';
import { Typography, Button, Box } from '@material-ui/core';
import COLORS from '../../config/colors';
import { Add } from '@material-ui/icons';
import ShieldGreenSVG from './ShieldGreen.svg';
import { FormattedMessage, useIntl } from 'react-intl';
import { Shipment } from 'services';
import { MixpanelContext } from 'config/analytics';
import { appendLocaleToUrl } from 'config/locale';
import { useLocale } from 'providers/I18nProvider';

export interface InsuranceNotificationProps {
  shipment: Shipment;
}

const KARLA_FONT_FAMILY = 'Karla, sans-serif';
const LEARN_MORE_URL =
  'https://support.easyship.com/hc/en-us/articles/32040478043410-Buyer-Shipping-Insurance-via-Tracking';

/**
 * Parses a currency string (e.g. "$12.34") and returns its numeric value (e.g. 12.34).
 */
const parseCurrency = (value: string): number => Number(value.replace(/[^0-9.-]+/g, ''));

const useStyles = makeStyles((theme: Theme) => ({
  container: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    padding: '24px 20px',
    backgroundColor: COLORS.white,
    borderRadius: 8,
    boxShadow: '0px 2px 4px rgba(40, 78, 102, 0.1)',
    border: `2px solid #00D9A3`,
  },
  shieldIcon: {
    width: 24,
    height: 24,
    flexShrink: 0,
    position: 'relative',
  },
  shieldIconImage: {
    width: 24,
    height: 24,
  },
  textContainer: {
    flex: '1 0 0',
    minWidth: 0,
  },
  mainText: {
    fontSize: 14,
    lineHeight: '20px',
    color: COLORS.inkDarkest,
    fontFamily: KARLA_FONT_FAMILY,
    margin: 0,
  },
  boldText: {
    fontWeight: 700,
    fontFamily: KARLA_FONT_FAMILY,
  },
  learnMoreLink: {
    display: 'inline-block',
    color: theme.palette.secondary.main,
    textDecoration: 'none',
    fontSize: 13,
    lineHeight: '20px',
    cursor: 'pointer',
    '&:hover': {
      textDecoration: 'underline',
    },
  },
  addButton: {
    height: 40,
    borderRadius: 4,
    background: `linear-gradient(to bottom, ${COLORS.white} 0%, ${COLORS.skyNormal} 100%)`,
    boxShadow: '0px 2px 4px rgba(40, 78, 102, 0.2)',
    color: COLORS.inkNormal,
    textTransform: 'uppercase',
    fontSize: 14,
    fontWeight: 700,
    fontFamily: KARLA_FONT_FAMILY,
    minWidth: 76,
    padding: '0 12px 0 8px',
    '&:hover': {
      background: COLORS.skyLight,
      boxShadow: '0px 2px 4px rgba(40, 78, 102, 0.2)',
    },
    '&:active': {
      background: COLORS.skyDark,
      boxShadow: '0px 4px 4px rgba(40, 78, 102, 0.2)',
    },
  },
  plusIconContainer: {
    width: 20,
    height: 20,
    position: 'relative',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    marginRight: 8,
  },
  plusIconImage: {
    width: 15,
    height: 15,
  },
}));

const InsuranceNotification: React.FC<InsuranceNotificationProps> = ({ shipment }) => {
  const {
    insurance_fee,
    insured_value,
    insurance_coverage,
    insurance_requested_by_buyer,
    has_paid_buyer_insurance_payment_intent,
    is_eligible_for_buyer_insurance,
    insurance_payment_portal_url,
  } = shipment;

  const classes = useStyles();
  const { formatMessage } = useIntl();
  const { locale } = useLocale();
  const mixpanel = useContext(MixpanelContext);

  const mixpanelProperties = useMemo(
    () => ({
      shipment_id: shipment.easyship_shipment_id,
      merchant_id: shipment.easyship_company_id,
      context: 'tracking_page',
      insured_value: parseCurrency(shipment.insured_value),
      quoted_premium: parseCurrency(shipment.insurance_fee),
      destination_country: shipment.destination_country.name,
    }),
    [shipment],
  );

  const isInsuredByBuyer = insurance_requested_by_buyer && has_paid_buyer_insurance_payment_intent;
  const isNotAvailable = !isInsuredByBuyer && !is_eligible_for_buyer_insurance;

  useEffect(() => {
    if (isNotAvailable || isInsuredByBuyer) {
      return;
    }

    mixpanel.identify(shipment.easyship_shipment_id);
    mixpanel.track('Insurance Module Seen', mixpanelProperties);
  }, [
    isInsuredByBuyer,
    is_eligible_for_buyer_insurance,
    mixpanel,
    mixpanelProperties,
    isNotAvailable,
    shipment.easyship_shipment_id,
  ]);

  const handleAddClick = useCallback(() => {
    mixpanel.track('Insurance Module Clicked', mixpanelProperties);
    window.open(appendLocaleToUrl(insurance_payment_portal_url, locale), '_blank');
  }, [mixpanel, mixpanelProperties, insurance_payment_portal_url, locale]);

  if (isNotAvailable) {
    return null;
  }

  return (
    <Box className={classes.container}>
      <div className={classes.shieldIcon}>
        <img
          src={ShieldGreenSVG}
          alt={formatMessage({ id: 'insurance.shield-alt' })}
          className={classes.shieldIconImage}
        />
      </div>

      <div className={classes.textContainer}>
        <Typography className={classes.mainText} component="p">
          <FormattedMessage
            id={`insurance.${isInsuredByBuyer ? 'insured' : 'not-insured'}`}
            values={{
              insuranceFee: insurance_fee,
              insuredValue: insured_value,
              insuranceCoverage: insurance_coverage,
              b: (chunks: React.ReactNode) => <span className={classes.boldText}>{chunks}</span>,
            }}
          />{' '}
          <a href={LEARN_MORE_URL} className={classes.learnMoreLink} rel="noopener noreferrer" target="_blank">
            <FormattedMessage id="global.learn-more" />
          </a>
        </Typography>
      </div>

      {!isInsuredByBuyer && is_eligible_for_buyer_insurance && (
        <Button
          className={classes.addButton}
          onClick={handleAddClick}
          variant="contained"
          disableElevation
          disableRipple
        >
          <div className={classes.plusIconContainer}>
            <Add />
          </div>
          <FormattedMessage id="global.add" />
        </Button>
      )}
    </Box>
  );
};

export default InsuranceNotification;
